import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { ApiService } from '@services/api.service';
import { MatDialogCustomComponent } from '../mat-dialogs/mat-dialog-custom/mat-dialog-custom.component';
import { BeneficiaryType } from '@constants/constants';
import { StorageService } from '@services/storage.service';

type BankAccount = {
  bankAccountId: string;
  alias: string;
  bankName: string;
  status: string;
  type: 'verified_provider' | string;
  country: string;
  state: string;
  currency: string;
  accountNumber: string;
  routingCode: string;
  swift: string;
  address: string;
  createdAt: string;
  updatedAt: string;
  beneficiaryTypeId: string;
  payee: {
    type: string;
    name: string;
    address: string;
    city: string;
    postalCode: string;
    country: string;
    state: string;
    email: string;
  };
  companySchool: string | null;
};

@Component({
  selector: 'ew-select-bank-account',
  templateUrl: './select-bank-account.component.html',
  styleUrls: ['./select-bank-account.component.scss'],
})
export class SelectBankAccountComponent implements OnInit, OnChanges {
  @Input() label: string = '';
  @Input() value!: string | null;
  @Input() width: string = '160px';
  @Input() returnFirstValue: boolean = false;
  @Input() lookupForProviders: boolean = false;
  @Input() limit?: number | null;
  @Input() version: number = 1;
  @Output() valueChanged = new EventEmitter<any>();

  bankAccountsBk: BankAccount[] = [];
  bankAccounts: BankAccount[] = [];
  bankAccount?: BankAccount;

  accountTypes: { label: string; value: 'company' | 'provider' }[] = [
    {
      label: 'settingsBankAccounts:buttonCreateCompanyAccount',
      value: 'company',
    },
    {
      label: 'settingsBankAccounts:buttonCreateExternalProvider',
      value: 'provider',
    },
  ];
  selectedAccountType?: string | null;
  loadingBankAccounts: boolean = false;

  constructor(
    private API: ApiService,
    private DIALOG: MatDialogRef<MatDialogCustomComponent>,
    private storageService: StorageService,
  ) {}

  closeDialog() {
    this.DIALOG.close();
  }

  /**
   * component start
   * @author Martin Batun Tec.
   */
  ngOnInit(): void {
    let PATH = `v1/bank-accounts?beneficiary=${
      !this.lookupForProviders
        ? `${BeneficiaryType.SAME_COMPANY}&status=verified`
        : `${BeneficiaryType.EXTERNAL_PROVIDER}&status=verified`
    }`;

    if (this.limit) {
      PATH += `&limit=${this.limit}`;
    }
    this.loadingBankAccounts = true;
    this.API.get(PATH).subscribe(
      (data: Partial<{ success: boolean; records: [BankAccount] | [] }>) => {
        if (!data.records) {
          data.records = [];
        }

        if (this.version == 1) {
          this.bankAccounts = data.records?.sort(
            ({ alias: a }, { alias: b }) => {
              if (a.toLowerCase() <= b.toLowerCase()) {
                return -1;
              }
              return 1;
            },
          );
        } else if (this.version == 2) {
          this.bankAccountsBk = data.records?.sort((a, b) =>
            a.alias.localeCompare(
              b.alias,
              this.storageService.getLanguage() || 'en',
              { sensitivity: 'base' },
            ),
          );
          this.filterBankAccounts();
        }

        if (this.returnFirstValue && this.bankAccounts.length) {
          this.valueChanged.next(this.bankAccounts[0]);
        }
        this.loadingBankAccounts = false;
      },
    );
  }

  /**
   * filter the list with the elements that match what you are looking for
   * @param search search value
   * @author Martin Batun Tec.
   */
  filterBankAccounts(search?: string | null): void {
    const filterValue = search?.toLowerCase() || '';
    this.bankAccounts = this.bankAccountsBk.filter((account) =>
      `${account.alias} ${account.bankName} ${account.accountNumber} ${account.currency} ${account.country}`
        .toLowerCase()
        .includes(filterValue),
    );
  }

  /**
   * returns the array of step validations
   * @param changes changes inputs
   * @author Martin Batun Tec.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['value']) {
      setTimeout(
        () => {
          this.bankAccount = this.bankAccounts.find(
            (e) => e.currency == changes['value'].currentValue,
          );
        },
        this.bankAccounts.length ? 0 : 3000,
      );
    }
  }
}
